import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Paperclip, ArrowLeft, Menu, X, Loader2, AlertCircle, Inbox, Send, Archive, RefreshCw, CornerDownLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('authToken')}` });

const TABS = [
  { id: 'inbox', label: 'Inbox', icon: Inbox, folder: 'INBOX' },
  { id: 'sent', label: 'Sent', icon: Send, folder: 'Sent' }
];

const EmailClient = () => {
  // Account selection
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Tab + list + selection
  const [tab, setTab] = useState('inbox');
  const [outreachOnly, setOutreachOnly] = useState(true); // default: only show inbox from people we've emailed
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'ai' | 'manual' — filters by AI-sent vs human-sent
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // uid for IMAP, sent_emails.id for log
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [outreachFiltered, setOutreachFiltered] = useState(false);

  // ------------- accounts -------------
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/list`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setAccounts(data.data);
        const smtp = data.data.filter(a => a.source === 'smtp');
        if (smtp.length > 0 && !selectedAccountId) {
          const primary = smtp.find(a => a.is_primary) || smtp[0];
          setSelectedAccountId(primary.id);
        }
      }
    } catch (err) { console.error('Accounts fetch failed', err); }
  }, [selectedAccountId]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const smtpAccounts = accounts.filter(a => a.source === 'smtp');
  const hasSmtp = smtpAccounts.length > 0;

  // ------------- list fetch -------------
  const fetchList = useCallback(async () => {
    setLoadingList(true); setError(null);
    setSelectedId(null); setSelectedDetail(null);
    try {
      if (tab === 'log') {
        const res = await fetch(`${API_URL}/api/fetchemail`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        if (!result.success) throw new Error(result.message || 'Failed to fetch');
        const list = result.data.map(e => ({
          id: `log-${e.id}`,
          uid: e.id,
          subject: e.subject,
          from: e.sender_email,
          to: e.recipient_email,
          preview: (e.message_body || '').substring(0, 100),
          date: e.sent_at,
          status: e.status,
          errorMessage: e.error_message,
          hasAttachment: !!e.attachments,
          source: 'log',
          raw: e
        }));
        setMessages(list);
      } else {
        if (!selectedAccountId) { setMessages([]); setLoadingList(false); return; }
        const folder = TABS.find(t => t.id === tab).folder;
        const params = new URLSearchParams({ accountId: selectedAccountId, folder, page: 0, pageSize: 50 });
        if (tab === 'inbox' && outreachOnly) params.set('outreachOnly', 'true');
        const res = await fetch(`${API_URL}/api/email-fetch/messages?${params}`, { credentials: 'include', headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        if (!result.success) throw new Error(result.message || 'Failed to fetch');
        setOutreachFiltered(!!result.data.outreachFiltered);
        const list = (result.data.messages || []).map(m => ({
          id: `imap-${m.uid}`,
          uid: m.uid,
          subject: m.subject,
          from: (m.from[0]?.address) || '',
          fromName: m.from[0]?.name || '',
          to: (m.to[0]?.address) || '',
          preview: '',
          date: m.date,
          seen: m.seen,
          replyTo: m.replyTo || null,
          source: 'imap',
          agentSource: m.agentSource || 'manual', // 'ai' | 'manual' — set by /messages annotation
          messageId: m.messageId
        }));
        setMessages(list);
      }
    } catch (err) {
      console.error('List fetch error', err);
      setError(err.message);
    }
    setLoadingList(false);
  }, [tab, selectedAccountId, outreachOnly]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ------------- detail fetch -------------
  const handleSelect = async (msg) => {
    setSelectedId(msg.id);
    if (window.innerWidth < 768) setIsMobileMenuOpen(false);

    if (msg.source === 'log') {
      setSelectedDetail(msg.raw); // already have full body
      return;
    }
    // IMAP — fetch full body
    setLoadingDetail(true);
    setSelectedDetail(null);
    try {
      const folder = TABS.find(t => t.id === tab).folder;
      const params = new URLSearchParams({ accountId: selectedAccountId, folder });
      const res = await fetch(`${API_URL}/api/email-fetch/message/${msg.uid}?${params}`, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      setSelectedDetail({ ...result.data, _replyTo: msg.replyTo });
    } catch (err) {
      setSelectedDetail({ _error: err.message });
    }
    setLoadingDetail(false);
  };

  // ------------- render helpers -------------
  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  };

  const renderListItem = (msg) => {
    const isSelected = selectedId === msg.id;
    const isFailed = msg.status === 'failed';
    return (
      <div
        key={msg.id}
        onClick={() => handleSelect(msg)}
        className={`p-4 cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-slate-800 ${
          isSelected ? 'bg-blue-50 dark:bg-slate-800 border-l-4 border-blue-500' : ''
        } ${isFailed ? 'bg-red-50 dark:bg-red-900/20' : ''} ${msg.source === 'imap' && !msg.seen ? 'font-semibold' : ''}`}
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-sm text-gray-900 dark:text-slate-100 flex items-center gap-2 truncate flex-1 min-w-0">
            {tab === 'inbox' ? (msg.fromName || msg.from || '(unknown)') : `To: ${msg.to || msg.from || '(unknown)'}`}
            {isFailed && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Failed</span>}
            {msg.replyTo && (
              <span title={`Reply to: ${msg.replyTo.subject}`} className="inline-flex items-center gap-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded">
                <CornerDownLeft className="w-3 h-3" /> Reply
              </span>
            )}
            {msg.agentSource === 'ai' && (
              <span title="Sent or replied via AI agent" className="inline-flex items-center gap-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded">
                🤖 AI
              </span>
            )}
          </h3>
          <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0 ml-2">{formatTime(msg.date)}</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm text-gray-800 dark:text-slate-200 flex-1 truncate">{msg.subject || '(no subject)'}</h4>
          {msg.hasAttachment && <Paperclip className="w-3 h-3 text-gray-400" />}
        </div>
        {msg.preview && <p className="text-sm text-gray-600 dark:text-slate-400 line-clamp-2">{msg.preview}</p>}
        <p className="text-xs text-gray-400 mt-1">{formatDate(msg.date)}</p>
      </div>
    );
  };

  const tabsDisabledReason = (tabId) => {
    if (tabId === 'log') return null;
    if (!hasSmtp) return 'Add an SMTP+IMAP account in Email Settings to enable';
    return null;
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-slate-950">
      {/* Top bar: tabs + account selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const disabledReason = tabsDisabledReason(t.id);
            return (
              <button
                key={t.id}
                disabled={!!disabledReason}
                onClick={() => !disabledReason && setTab(t.id)}
                title={disabledReason || ''}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  tab === t.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : disabledReason
                      ? 'text-gray-300 dark:text-slate-600 cursor-not-allowed'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {hasSmtp && tab !== 'log' && (
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-md p-1">
              {['all', 'ai', 'manual'].map(s => (
                <button
                  key={s}
                  onClick={() => setSourceFilter(s)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                    sourceFilter === s
                      ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700'
                  }`}
                  title={
                    s === 'all' ? 'Show all messages'
                    : s === 'ai' ? 'Show only AI-drafted messages and their replies'
                    : 'Show only manually-sent messages'
                  }
                >
                  {s === 'all' ? 'All' : s === 'ai' ? '🤖 AI' : '✋ Manual'}
                </button>
              ))}
            </div>
          )}
          {tab === 'inbox' && hasSmtp && (
            <button
              onClick={() => setOutreachOnly(v => !v)}
              title={outreachOnly ? 'Showing only inbox messages from people you\'ve emailed (and replies to your outreach). Click to show full inbox.' : 'Showing your full inbox. Click to filter to outreach contacts only.'}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                outreachOnly
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-800'
                  : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-400 border-gray-200 dark:border-slate-600 hover:border-purple-300'
              }`}
            >
              {outreachOnly ? '✓ Outreach only' : 'Show full inbox'}
            </button>
          )}
          {tab !== 'log' && smtpAccounts.length > 1 && (
            <select
              value={selectedAccountId || ''}
              onChange={e => setSelectedAccountId(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100"
            >
              {smtpAccounts.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
            </select>
          )}
          <button onClick={fetchList} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile bar */}
      <div className="md:hidden bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between p-3">
          {selectedId ? (
            <button onClick={() => { setSelectedId(null); setSelectedDetail(null); setIsMobileMenuOpen(true); }} className="flex items-center gap-2 text-blue-600">
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
          ) : (
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg">
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {/* List panel */}
        <div className={`
          fixed md:relative md:block w-full md:w-96 h-full
          bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700 overflow-y-auto
          transition-transform duration-300 ease-in-out
          ${(isMobileMenuOpen || !selectedId) ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          z-40 md:z-auto
        `}>
          {loadingList ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <span className="ml-3 text-gray-500 text-sm">Loading…</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-gray-700 dark:text-slate-300">{error}</p>
              <button onClick={fetchList} className="mt-3 px-3 py-1.5 bg-blue-600 text-white text-sm rounded">Retry</button>
            </div>
          ) : (() => {
            const filtered = sourceFilter === 'all'
              ? messages
              : messages.filter(m => (m.agentSource || 'manual') === sourceFilter);
            return filtered.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Inbox className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No messages</h3>
                <p className="text-gray-500 text-sm">
                  {sourceFilter === 'ai' ? 'No AI-drafted messages in this view yet.'
                   : sourceFilter === 'manual' ? 'No manual messages in this view.'
                   : tab === 'inbox' ? (outreachOnly
                       ? 'No replies or correspondence yet from people you\'ve emailed.'
                       : 'Your inbox is empty.')
                   : tab === 'sent' ? 'No sent messages found.' : 'You haven\'t sent any emails yet.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-slate-800">
                {filtered.map(renderListItem)}
              </div>
            );
          })()}
        </div>

        {/* Detail panel */}
        <div className={`flex-1 bg-white dark:bg-slate-900 ${selectedId ? 'block' : 'hidden md:block'} overflow-y-auto`}>
          {!selectedId ? (
            <div className="hidden md:flex h-full items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Inbox className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">Select a message</h3>
                <p className="text-gray-500">Choose a message from the list to view it</p>
              </div>
            </div>
          ) : loadingDetail ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : selectedDetail?._error ? (
            <div className="p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">{selectedDetail._error}</p>
            </div>
          ) : selectedDetail ? (
            <DetailView detail={selectedDetail} tab={tab} />
          ) : null}
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => setIsMobileMenuOpen(false)} />
        )}
      </div>
    </div>
  );
};

// ---------- Detail subcomponent ----------
const DetailView = ({ detail, tab }) => {
  // App-sent log shape
  if (tab === 'log') {
    const e = detail;
    return (
      <div className="h-full flex flex-col">
        <div className="border-b border-gray-200 dark:border-slate-700 p-4 md:p-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-3">{e.subject}</h1>
          {e.status === 'failed' && (
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">Failed to Send</span>
          )}
          <div className="space-y-1 text-sm text-gray-600 dark:text-slate-400 mt-3">
            <div><span className="font-medium text-gray-900 dark:text-slate-100">From:</span> {e.sender_email}</div>
            <div><span className="font-medium text-gray-900 dark:text-slate-100">To:</span> {e.recipient_email}</div>
            {e.cc_email && <div><span className="font-medium text-gray-900 dark:text-slate-100">CC:</span> {e.cc_email}</div>}
            <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {new Date(e.sent_at).toLocaleString()}</div>
          </div>
        </div>
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          {e.status === 'failed' && e.error_message && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-lg text-sm text-red-700 dark:text-red-400">
              {e.error_message}
            </div>
          )}
          <div className="text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed text-sm md:text-base">
            {e.message_body}
          </div>
        </div>
      </div>
    );
  }

  // IMAP message
  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-gray-200 dark:border-slate-700 p-4 md:p-6">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-3">{detail.subject || '(no subject)'}</h1>
        {detail._replyTo && (
          <div className="mb-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg flex items-start gap-2">
            <CornerDownLeft className="w-4 h-4 text-purple-600 mt-0.5" />
            <div className="text-sm">
              <p className="text-purple-900 dark:text-purple-200 font-medium">This is a reply to your outreach</p>
              <p className="text-purple-700 dark:text-purple-400 text-xs mt-0.5">
                Original: <span className="font-medium">{detail._replyTo.subject}</span> · sent to <span className="font-medium">{detail._replyTo.recipient_email}</span>
              </p>
            </div>
          </div>
        )}
        <div className="space-y-1 text-sm text-gray-600 dark:text-slate-400">
          <div>
            <span className="font-medium text-gray-900 dark:text-slate-100">From:</span>{' '}
            {(detail.from || []).map(a => a.name ? `${a.name} <${a.address}>` : a.address).join(', ')}
          </div>
          <div>
            <span className="font-medium text-gray-900 dark:text-slate-100">To:</span>{' '}
            {(detail.to || []).map(a => a.name ? `${a.name} <${a.address}>` : a.address).join(', ')}
          </div>
          {detail.cc?.length > 0 && (
            <div>
              <span className="font-medium text-gray-900 dark:text-slate-100">CC:</span>{' '}
              {detail.cc.map(a => a.name ? `${a.name} <${a.address}>` : a.address).join(', ')}
            </div>
          )}
          <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {detail.date ? new Date(detail.date).toLocaleString() : ''}</div>
        </div>
      </div>
      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        {detail.html ? (
          <div className="prose dark:prose-invert max-w-3xl text-gray-800 dark:text-slate-200" dangerouslySetInnerHTML={{ __html: detail.html }} />
        ) : (
          <div className="text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed text-sm md:text-base max-w-3xl">
            {detail.text || '(no body)'}
          </div>
        )}
        {detail.attachments?.length > 0 && (
          <div className="mt-6 space-y-2 max-w-3xl">
            <h4 className="text-sm font-medium text-gray-900 dark:text-slate-100">Attachments ({detail.attachments.length})</h4>
            {detail.attachments.map((a, i) => (
              <div key={i} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 flex items-center gap-3">
                <Paperclip className="w-4 h-4 text-blue-600" />
                <div className="text-sm">
                  <p className="font-medium text-gray-900 dark:text-slate-100">{a.filename || 'attachment'}</p>
                  <p className="text-xs text-gray-500">{a.contentType} · {((a.size || 0) / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailClient;
